import { create } from "zustand";
import { Track } from "@/types/track";
import { fetchVersionsForTracks, attachTrackVersions } from "./track-versions";
import { supabase } from "./supabase";
import { UserCacheService } from "./user-cache";
import { excludeLabels, getExcludedLabels } from "./excluded-labels"; // [EXCLUDE_LABELS]
import { ilikeContainsPattern, toOrFilterSearchTerm } from "./search-ranking";
import {
  INSTRUMENT_OPTIONS,
  KEY_OPTIONS,
  LANGUAGE_OPTIONS,
  facetValues,
} from "./catalog-facets";

export type SortOption =
  | "relevance" // "Best match" — ranked by match quality while a search is active
  | "title-asc"
  | "title-desc"
  | "artist-asc"
  | "artist-desc"
  | "release-newest"
  | "release-oldest";

export type LicensingTier =
  | "all"
  | "instant"
  | "standard"
  | "bespoke"
  | "unlicensed";

type Scope = "mine" | "label" | "global";

/** A scope with the id it filters on, resolved once so both query paths select the same rows. */
type ResolvedScope =
  | { kind: "mine"; userId: string }
  | { kind: "label"; labelId: string }
  | { kind: "global" };

/**
 * The Instruments, Key, Language and Label filters: selected option ids (catalog-facets.ts), or
 * label ids for `labels`. `search_tracks_ranked` has no parameters for them, so while one is
 * active the grid reads the sorted query instead of "Best match" (see `hasFacetFilters`).
 */
export interface FacetFilters {
  instruments: string[];
  keys: string[];
  languages: string[];
  labels: string[];
}

export const NO_FACET_FILTERS: FacetFilters = {
  instruments: [],
  keys: [],
  languages: [],
  labels: [],
};

/** Every filter the grid and the header counts apply on top of scope, tier and search. */
export interface TrackFilters extends FacetFilters {
  genres: string[];
  moods: string[];
}

interface TracksQuery extends TrackFilters {
  scope: Scope;
  sort: SortOption;
  search: string;
  tier: LicensingTier;
  pageIndex: number;
  pageSize: number;
  labelId?: string;
}

interface CatalogStatus {
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadedOnce: boolean;
}

interface CatalogState {
  tracks: Track[];
  query: TracksQuery;
  status: CatalogStatus;
  _generation: number;
  setQuery: (partial: Partial<TracksQuery>) => void;
  reset: () => void;
  fetchPage: (reset: boolean) => Promise<void>;
  removeTrack: (trackId: string | number) => void;
}

const PAGE_SIZE = 30;

/**
 * PostgREST `.or()` filter for a search on title, artist or album. The term matches literally
 * (escaped), exactly as `search_tracks_ranked` matches it, so the header counts, the sorted grid
 * and the ranked grid select the same rows. `term` must come from `toOrFilterSearchTerm`.
 */
export function trackSearchOrFilter(term: string): string {
  const pattern = ilikeContainsPattern(term);
  return `title.ilike.${pattern},artist.ilike.${pattern},album.ilike.${pattern}`;
}

/**
 * Whether an Instruments, Key, Language or Label filter is active. `search_tracks_ranked` has no
 * parameters for these, so while one is, the grid reads the sorted path and the page does not offer
 * "Best match": the filters always apply, and the counts (same clauses) match the grid.
 */
export function hasFacetFilters(f: FacetFilters): boolean {
  return (
    f.instruments.length > 0 ||
    f.keys.length > 0 ||
    f.languages.length > 0 ||
    f.labels.length > 0
  );
}

/** The tracks query operators the filters use; both the grid's and the counts' builders have them. */
interface TrackFilterable<Self> {
  overlaps(column: "genre" | "instruments", value: readonly string[]): Self;
  contains(column: "moods", value: string): Self;
  in(column: "key" | "label_tag", values: readonly string[]): Self;
  or(filters: string): Self;
}

/**
 * Applies the genre, mood, instrument, key, language and label filters, each only when set. The
 * sorted grid query and the page's header counts both call this, so they always select the same
 * rows. Within a filter, options match ANY of their values (OR); separate filters combine with AND.
 *
 * - Instruments: `overlaps` with every stored spelling of the chosen families (`instruments` is
 *   text[]; `contains` with a JSON string was rejected by PostgREST with 400 "malformed array literal").
 * - Key: `in` with both enharmonic spellings of the chosen keys.
 * - Language: one `.or()` of substring matches. A second `or` parameter is ANDed with the
 *   search's `.or()`, so it never widens the search.
 * - Label: `in` on `label_tag`.
 */
export function applyTrackFilters<Q extends TrackFilterable<Q>>(q: Q, f: TrackFilters): Q {
  let out = q;
  if (f.genres.length > 0) {
    out = out.overlaps("genre", f.genres);
  }
  if (f.moods.length > 0) {
    out = out.contains("moods", JSON.stringify(f.moods));
  }
  const instruments = facetValues(INSTRUMENT_OPTIONS, f.instruments);
  if (instruments.length > 0) {
    out = out.overlaps("instruments", instruments);
  }
  const keys = facetValues(KEY_OPTIONS, f.keys);
  if (keys.length > 0) {
    out = out.in("key", keys);
  }
  const languages = facetValues(LANGUAGE_OPTIONS, f.languages);
  if (languages.length > 0) {
    out = out.or(languages.map((value) => `language.ilike.${ilikeContainsPattern(value)}`).join(","));
  }
  if (f.labels.length > 0) {
    out = out.in("label_tag", f.labels);
  }
  return out;
}

/**
 * "Best match": one page ranked by `search_tracks_ranked` — a track ranks by its best match on
 * title, artist or album (exact > starts with > a word starts with > contains). Scope, tier, genre
 * and mood are the sorted query's filters, applied inside the function; an omitted arg takes the
 * function's default. It has no facet parameters, so `fetchPage` never ranks while one is set.
 */
function rankedTracksPage(
  scope: ResolvedScope,
  query: TracksQuery,
  term: string,
  start: number
) {
  const excludedLabels = getExcludedLabels(); // [EXCLUDE_LABELS]
  return supabase.rpc("search_tracks_ranked", {
    p_search: term,
    p_label_tag: scope.kind === "label" ? scope.labelId : undefined,
    // The function filters on auth.uid() — the same user UserCacheService resolved.
    p_own_tracks_only: scope.kind === "mine",
    p_published_only: scope.kind !== "mine",
    // Version families collapse to their main work on both paths (see sortedTracksPage).
    p_main_works_only: true,
    p_exclude_labels:
      scope.kind === "global" && excludedLabels.length > 0 ? excludedLabels : undefined, // [EXCLUDE_LABELS]
    p_tier: query.tier === "all" ? undefined : query.tier,
    p_genres: query.genres.length > 0 ? query.genres : undefined,
    p_moods: query.moods.length > 0 ? query.moods : undefined,
    p_limit: query.pageSize,
    p_offset: start,
  });
}

/** One page ordered by the chosen sort, optionally filtered by a search term. */
function sortedTracksPage(
  scope: ResolvedScope,
  query: TracksQuery,
  term: string,
  start: number
) {
  let dbQuery = supabase.from("tracks").select("*");

  if (scope.kind === "mine") {
    dbQuery = dbQuery.eq("user_id", scope.userId);
  } else if (scope.kind === "global") {
    // Buyers: all published tracks, no label filter
    dbQuery = excludeLabels(dbQuery.eq("is_published", true)); // [EXCLUDE_LABELS]
  } else {
    dbQuery = dbQuery.eq("label_tag", scope.labelId).eq("is_published", true);
  }

  // Version families collapse to their main work: a stem, instrumental or 30-second cut
  // is not a separate catalog item. 8,322 of Felt's 13,137 published tracks (63%) are
  // versions, which is why the grid showed "A Deadly Game" three times.
  //
  // Applied to EVERY scope deliberately. This is one grid — collapsing only for sellers
  // would show buyers and sellers a different catalog on the same page.
  dbQuery = dbQuery.is("parent_track_id", null);

  if (query.tier !== "all") {
    dbQuery = dbQuery.eq("licensing_tier", query.tier);
  }

  dbQuery = applyTrackFilters(dbQuery, query);

  if (term) {
    dbQuery = dbQuery.or(trackSearchOrFilter(term));
  }

  let sortColumn = "created_at";
  let sortAscending = false;
  switch (query.sort) {
    // "Best match" with no search term has nothing to rank: it orders like the page's
    // default sort, title A-Z — never by the created_at fallback below.
    case "relevance":
    case "title-asc":
      sortColumn = "title";
      sortAscending = true;
      break;
    case "title-desc":
      sortColumn = "title";
      sortAscending = false;
      break;
    case "artist-asc":
      sortColumn = "artist";
      sortAscending = true;
      break;
    case "artist-desc":
      sortColumn = "artist";
      sortAscending = false;
      break;
    case "release-oldest":
      sortColumn = "created_at";
      sortAscending = true;
      break;
    default:
      sortColumn = "created_at";
      sortAscending = false;
  }
  dbQuery = dbQuery.order(sortColumn, { ascending: sortAscending });
  // Tiebreaker: none of the sort columns are unique (Felt's catalog has 4,693
  // distinct titles across 13,058 tracks), and OFFSET paging issues each page as
  // a separate query. Without a unique last key Postgres is free to order tied
  // rows differently per page, so a row can repeat on two pages or be skipped
  // entirely. `id` is the primary key, so it makes the total order deterministic.
  dbQuery = dbQuery.order("id", { ascending: true });

  return dbQuery.range(start, start + query.pageSize - 1);
}

/**
 * The newest published main works across every label: the first page of the grid's global
 * "release-newest" query (same clauses: published, main works only, excluded labels left out,
 * `created_at DESC, id ASC`). The home page's "New in the catalog" shelf reads it.
 */
export function latestPublishedTracks(limit: number) {
  return sortedTracksPage(
    { kind: "global" },
    {
      scope: "global",
      sort: "release-newest",
      search: "",
      genres: [],
      moods: [],
      ...NO_FACET_FILTERS,
      tier: "all",
      pageIndex: 0,
      pageSize: limit,
    },
    "",
    0
  );
}

export const useCatalogStore = create<CatalogState>((set, get) => ({
  tracks: [],
  query: {
    scope: "label",
    sort: "release-newest",
    search: "",
    genres: [],
    moods: [],
    ...NO_FACET_FILTERS,
    tier: "all",
    pageIndex: 0,
    pageSize: PAGE_SIZE,
    labelId: undefined,
  },
  status: {
    loading: false,
    error: null,
    hasMore: true,
    loadedOnce: false,
  },
  _generation: 0,

  setQuery: (partial) =>
    set((state) => ({
      query: { ...state.query, ...partial, pageIndex: 0 },
    })),

  reset: () =>
    set((state) => ({
      tracks: [],
      query: { ...state.query, pageIndex: 0 },
      status: { loading: false, error: null, hasMore: true, loadedOnce: false },
      _generation: state._generation + 1,
    })),

  fetchPage: async (reset) => {
    const { query, status, tracks: currentTracks, _generation: gen } = get();
    if (status.loading) return;

    set({ status: { ...status, loading: true, error: null } });

    try {
      const user = await UserCacheService.getUser();

      let scope: ResolvedScope;
      if (query.scope === "mine") {
        if (!user?.id) {
          throw new Error("User not authenticated");
        }
        scope = { kind: "mine", userId: user.id };
      } else if (query.scope === "global") {
        scope = { kind: "global" };
      } else {
        // "label" scope - sellers only
        if (!query.labelId) {
          if (get()._generation !== gen) return;
          set({
            tracks: [],
            status: { loading: false, error: null, hasMore: false, loadedOnce: true },
          });
          return;
        }
        scope = { kind: "label", labelId: query.labelId };
      }

      const term = toOrFilterSearchTerm(query.search);
      const pageIndex = reset ? 0 : query.pageIndex;
      const start = pageIndex * query.pageSize;

      // A facet filter forces the sorted path: the ranked function would ignore it.
      const ranked = query.sort === "relevance" && term !== "" && !hasFacetFilters(query);
      const { data, error } = await (ranked
        ? rankedTracksPage(scope, query, term, start)
        : sortedTracksPage(scope, query, term, start));
      if (error) throw error;

      // Discard results if a newer reset/query has started since this fetch began
      if (get()._generation !== gen) return;

      // Build a Set of existing track IDs for O(1) lookup
      const existingIds = reset ? new Set() : new Set(currentTracks.map(t => t.id));

      // Filter out duplicates within page AND against existing tracks.
      // Rows come back as the tracks table shape; the catalog consumes them as the
      // app Track view (same fields + optional computed extras) — assert at the boundary.
      const deduped = ((data || []) as unknown as Track[]).filter(
        (track, idx, arr) => {
          // Remove duplicates within fetched page
          if (arr.findIndex((t) => t.id === track.id) !== idx) return false;
          // Skip tracks that already exist in currentTracks (preserve references)
          if (existingIds.has(track.id)) return false;
          return true;
        }
      );

      // One batched query per page attaches every version family on this page, so a card
      // knows its family size before anyone opens the sheet.
      const withVersions = attachTrackVersions(
        deduped,
        await fetchVersionsForTracks(deduped.map((t) => String(t.id)))
      );

      // A newer reset/query may have landed during that fetch.
      if (get()._generation !== gen) return;

      const nextTracks = reset ? withVersions : [...currentTracks, ...withVersions];
      // Derive hasMore from what the server actually returned, not from the deduped
      // count. The dedup above is a safety net; letting it decide hasMore means a
      // single repeated row permanently ends pagination with thousands of tracks
      // still unfetched.
      const hasMore = (data?.length ?? 0) === query.pageSize;

      set({
        tracks: nextTracks,
        query: { ...query, pageIndex: hasMore ? pageIndex + 1 : pageIndex },
        status: {
          loading: false,
          error: null,
          hasMore,
          loadedOnce: true,
        },
      });
    } catch (err: any) {
      // Don't write error if a newer reset/query has superseded this fetch
      if (get()._generation !== gen) return;
      set({
        status: {
          loading: false,
          error: err?.message || "Failed to fetch tracks",
          hasMore: false,
          loadedOnce: true,
        },
      });
    }
  },

  removeTrack: (trackId) =>
    set((state) => ({
      tracks: state.tracks.filter((t) => String(t.id) !== String(trackId)),
    })),
}));
